import {
  convertCentsToUnit,
  format,
  formatDate,
} from '@monofront/business/utils';
import { IDaySlot, ShippingMethodConfiguration } from '@monofront/types';
import { useTranslations } from 'next-intl';
import { isNumber } from 'radash';
import {
  calculateDateDifferenceInDays,
  findClosestAvailableSlotToDate,
  findNextAvailableSlotDate,
} from '@monofront/business/utils';

const subtitleSerializer = (subtitle = '', deliveryDates: IDaySlot[]) => {
  const hasNextAvailablePlaceholders =
    subtitle.includes('%NEXT_AVAILABLE_DATE%') ||
    subtitle.includes('%NEXT_AVAILABLE_DAY%') ||
    subtitle.includes('%NEXT_AVAILABLE_DAY_PLUS_ONE%');

  if (!hasNextAvailablePlaceholders) return subtitle;

  if (!subtitle || deliveryDates?.length === 0) return '';
  const today = new Date();

  const nextAvailableDayWithSlots = findClosestAvailableSlotToDate(
    deliveryDates,
    today,
  );

  const nextDayWithSlotInDays = nextAvailableDayWithSlots?.date
    ? calculateDateDifferenceInDays(
        new Date(nextAvailableDayWithSlots?.date),
        today,
      )
    : null;

  const nextSlotInDaysPlusOne = isNumber(nextDayWithSlotInDays)
    ? nextDayWithSlotInDays + 1
    : null;

  if (!nextDayWithSlotInDays || !nextSlotInDaysPlusOne) return '';

  const nextAvailableSlotDate = nextAvailableDayWithSlots
    ? findNextAvailableSlotDate(nextAvailableDayWithSlots)
    : null;

  return subtitle
    .replace(
      '%NEXT_AVAILABLE_DATE%',
      nextAvailableSlotDate
        ? formatDate(nextAvailableSlotDate, {
            dateStyle: 'full',
            timeStyle: 'short',
          })
        : '',
    )
    .replace('%NEXT_AVAILABLE_DAY%', String(nextDayWithSlotInDays))
    .replace('%NEXT_AVAILABLE_DAY_PLUS_ONE%', String(nextSlotInDaysPlusOne));
};

type PriceWithThreshold = { price: number; threshold: number } | null;

const getHighestThreshold = (thresholds: { threshold: number }[]) => {
  if (!thresholds?.length) return null;

  return thresholds.reduce((highest, current) =>
    current.threshold > highest.threshold ? current : highest,
  );
};
const findCheapestThreshold = (
  configurations: ShippingMethodConfiguration[],
): PriceWithThreshold => {
  let cheapestThreshold: PriceWithThreshold = null;

  if (!configurations?.length) return cheapestThreshold;

  configurations.forEach((configuration) => {
    if (!configuration) return null;

    const { price, threshold } = configuration?.thresholds
      ? configuration.thresholds
          .map((t) => ({
            price: t.underThresholdAmount,
            threshold: t.threshold,
          }))
          .concat({
            price: configuration.aboveAllThresholdsAmount || 0,
            threshold:
              getHighestThreshold(configuration.thresholds)?.threshold || 0,
          })
          .reduce((cheapest, current) =>
            current.price < cheapest.price ? current : cheapest,
          )
      : {
          price: configuration.aboveAllThresholdsAmount,
          threshold: configuration.cartTotalThreshold || 0,
        };

    if (
      !cheapestThreshold ||
      price < cheapestThreshold.price ||
      (price === cheapestThreshold.price &&
        threshold < cheapestThreshold.threshold)
    ) {
      cheapestThreshold = { price, threshold };
    }
  });

  return cheapestThreshold;
};

export const useSerializers = () => {
  const t = useTranslations('DeliveryBar.ShippingMethodSelection.Table');

  const descriptionSerializer = (
    configurations: ShippingMethodConfiguration[],
    description: string,
  ) => {
    if (!description) return '';

    const cheapestThreshold = findCheapestThreshold(configurations) || {
      price: 0,
      threshold: 0,
    };

    return description
      .replace(
        '%MINIMUM_PRICE_AMOUNT%',
        cheapestThreshold.price === 0
          ? t('free')
          : format(convertCentsToUnit(cheapestThreshold.price)),
      )
      .replace(
        '%MINIMUM_PRICE_AMOUNT_THRESHOLD%',
        format(convertCentsToUnit(cheapestThreshold.threshold), {
          minimumFractionDigits: 0,
        }),
      );
  };

  return { descriptionSerializer, subtitleSerializer };
};
